/**
 * Capitalizes the first letter of a string, makes the rest of the string lower
 * case, and replaces underscores with spaces to humanize the string. Currently
 * designed for snake case strings such as APPLE_PIE and apple_strudel. This can
 * be expanded to handle other formatting styles.
 *
 * @param {string} str - The string to be humanized and capitalized.
 * @param {boolean} shouldCapitalizeAllWords - Whether to capitalize all words
 *   in the string.
 * @param {boolean} handleAcronyms - Whether to avoid lowercasing acronyms (2 to
 *   4 capitalized letters).
 * @returns {string} The humanized and capitalized string.
 */
export const humanizeString = (
  str: string,
  shouldCapitalizeAllWords: boolean = false,
  handleAcronyms: boolean = false,
): string => {
  // Replace underscores with spaces
  const humanizedStr = str.replace(/_/g, ' ')

  // List of specific words to ignore for acronym handling
  const nonAcronyms = ['AID', 'NEW', 'SAN', 'THE']
  // Common 2-letter acronyms to preserve (case-insensitive check, returns uppercase)
  const twoLetterAcronyms = ['QA', 'ID', 'HR', 'PR', 'IT', 'NA', 'TD']

  const capitalizeWord = (word: string): string => {
    // Check for 2-letter acronyms first (case-insensitive)
    const upperWord = word.toUpperCase()
    if (handleAcronyms && twoLetterAcronyms.includes(upperWord)) {
      return upperWord // Return uppercase acronym
    }
    if (
      handleAcronyms &&
      !nonAcronyms.includes(word) &&
      /^[A-Z0-9]{3}$/.test(word)
    ) {
      return word // Leave 3-letter acronyms as-is
    }
    // capitalize first letter, and make the rest of the string lower case
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  }

  if (shouldCapitalizeAllWords) {
    return humanizedStr.split(' ').map(capitalizeWord).join(' ')
  }

  // Split the string and only apply capitalization to the first word, with acronym handling
  const words = humanizedStr.split(' ')
  words[0] = capitalizeWord(words[0]) // Apply acronym logic to the first word

  // Lowercase the rest of the string unless it's an acronym and handleAcronyms is true
  for (let i = 1; i < words.length; i++) {
    const upperWord = words[i].toUpperCase()
    words[i] =
      handleAcronyms && twoLetterAcronyms.includes(upperWord)
        ? upperWord // Return uppercase 2-letter acronym
        : handleAcronyms &&
            !nonAcronyms.includes(words[i]) &&
            /^[A-Z0-9]{3}$/.test(words[i])
          ? words[i] // Keep 3-letter acronyms uppercase
          : words[i].toLowerCase() // Lowercase other words
  }

  return words.join(' ')
}
